import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { matrimoniosAPI } from "@/lib/api";
import { ArrowLeft, Save, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export default function MatrimonioFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    numero_libro: "",
    numero_folio: "",
    numero_partida: "",
    fecha_matrimonio: "",
    lugar_matrimonio: "",
    esposo_nombre: "",
    esposo_apellido: "",
    esposo_fecha_nacimiento: "",
    esposo_lugar_nacimiento: "",
    esposo_estado_civil_previo: "",
    esposa_nombre: "",
    esposa_apellido: "",
    esposa_fecha_nacimiento: "",
    esposa_lugar_nacimiento: "",
    esposa_estado_civil_previo: "",
    testigo1_nombre: "",
    testigo2_nombre: "",
    ministro_nombre: "",
    notas_marginales: "",
  });

  useEffect(() => {
    if (isEditing) {
      loadMatrimonio();
    }
  }, [id]);

  const loadMatrimonio = async () => {
    try {
      const response = await matrimoniosAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error("Error al cargar el matrimonio");
      navigate("/matrimonios");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await matrimoniosAPI.update(id, formData);
        toast.success("Matrimonio actualizado correctamente");
      } else {
        await matrimoniosAPI.create(formData);
        toast.success("Matrimonio registrado correctamente");
      }
      navigate("/matrimonios");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el matrimonio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="matrimonio-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/matrimonios")}
          className="h-10 w-10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <HeartHandshake className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
            {isEditing ? "Editar Matrimonio" : "Nuevo Matrimonio"}
          </h1>
          <p className="text-stone-600 mt-1">
            {isEditing ? "Modifica los datos del registro" : "Ingresa los datos del nuevo matrimonio"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Registro
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="numero_libro">Número de Libro *</Label>
              <Input
                id="numero_libro"
                name="numero_libro"
                value={formData.numero_libro}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_folio">Número de Folio *</Label>
              <Input
                id="numero_folio"
                name="numero_folio"
                value={formData.numero_folio}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_partida">Número de Partida *</Label>
              <Input
                id="numero_partida"
                name="numero_partida"
                value={formData.numero_partida}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_matrimonio">Fecha del Matrimonio *</Label>
              <Input
                id="fecha_matrimonio"
                name="fecha_matrimonio"
                type="date"
                value={formData.fecha_matrimonio}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="lugar_matrimonio">Lugar del Matrimonio *</Label>
              <Input
                id="lugar_matrimonio"
                name="lugar_matrimonio"
                value={formData.lugar_matrimonio}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos del Esposo */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Esposo
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="esposo_nombre">Nombre *</Label>
              <Input
                id="esposo_nombre"
                name="esposo_nombre"
                value={formData.esposo_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposo_apellido">Apellido *</Label>
              <Input
                id="esposo_apellido"
                name="esposo_apellido"
                value={formData.esposo_apellido}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposo_fecha_nacimiento">Fecha de Nacimiento</Label>
              <Input
                id="esposo_fecha_nacimiento"
                name="esposo_fecha_nacimiento"
                type="date"
                value={formData.esposo_fecha_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposo_lugar_nacimiento">Lugar de Nacimiento</Label>
              <Input
                id="esposo_lugar_nacimiento"
                name="esposo_lugar_nacimiento"
                value={formData.esposo_lugar_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="esposo_estado_civil_previo">Estado Civil Previo</Label>
              <Input
                id="esposo_estado_civil_previo"
                name="esposo_estado_civil_previo"
                value={formData.esposo_estado_civil_previo}
                onChange={handleChange}
                placeholder="Soltero, Viudo, etc."
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos de la Esposa */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos de la Esposa
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="esposa_nombre">Nombre *</Label>
              <Input
                id="esposa_nombre"
                name="esposa_nombre"
                value={formData.esposa_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposa_apellido">Apellido *</Label>
              <Input
                id="esposa_apellido"
                name="esposa_apellido"
                value={formData.esposa_apellido}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposa_fecha_nacimiento">Fecha de Nacimiento</Label>
              <Input
                id="esposa_fecha_nacimiento"
                name="esposa_fecha_nacimiento"
                type="date"
                value={formData.esposa_fecha_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="esposa_lugar_nacimiento">Lugar de Nacimiento</Label>
              <Input
                id="esposa_lugar_nacimiento"
                name="esposa_lugar_nacimiento"
                value={formData.esposa_lugar_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="esposa_estado_civil_previo">Estado Civil Previo</Label>
              <Input
                id="esposa_estado_civil_previo"
                name="esposa_estado_civil_previo"
                value={formData.esposa_estado_civil_previo}
                onChange={handleChange}
                placeholder="Soltera, Viuda, etc."
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Testigos y Ministro */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Testigos y Ministro
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="testigo1_nombre">Testigo 1</Label>
                <Input
                  id="testigo1_nombre"
                  name="testigo1_nombre"
                  value={formData.testigo1_nombre}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="testigo2_nombre">Testigo 2</Label>
                <Input
                  id="testigo2_nombre"
                  name="testigo2_nombre"
                  value={formData.testigo2_nombre}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ministro_nombre">Nombre del Ministro *</Label>
              <Input
                id="ministro_nombre"
                name="ministro_nombre"
                value={formData.ministro_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas_marginales">Notas Marginales</Label>
              <Textarea
                id="notas_marginales"
                name="notas_marginales"
                value={formData.notas_marginales}
                onChange={handleChange}
                rows={4}
                className="resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/matrimonios")}
            className="h-11"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-11 bg-rose-950 hover:bg-rose-900 text-white"
            data-testid="guardar-matrimonio-btn"
          >
            {loading ? (
              <span className="animate-pulse">Guardando...</span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Actualizar" : "Guardar"} Matrimonio
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
